import {
  APP_INITIALIZER,
  CUSTOM_ELEMENTS_SCHEMA,
  NgModule,
} from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AppRoutingModule } from './app-routing.module';
import { SharedModule } from '@app/shared/shared.module';

import { EffectsModule, provideEffects } from '@ngrx/effects';
import { provideStore, StoreModule } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { storageSyncMetaReducer } from 'ngrx-store-persist';

import { register as swiperRegister } from 'swiper/element/bundle';

import { environment } from '../environment/environment';
import { AppComponent } from './app.component';
import { HomeComponent } from './pages/home/home.component';
import { DappComponent } from './pages/dapp/dapp.component';
import { ExploreComponent } from './pages/explore/explore.component';
import { VaultComponent } from './pages/vault/vault.component';
import { AuthService } from './core/services/auth.service';

import { ApiModule, ReadyGGApiConfiguration } from '@readygg/ng-api';
import { UserEffects } from './state/effects/user.effects';
import { MarketPlaceEffects } from './state/effects/marketplace.effects';
import { reducers } from './state/reducers';
import { PaymentEffects } from './state/effects/payment.effects';
import { getApp, initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { provideAuth, getAuth } from '@angular/fire/auth';
import { provideStorage, getStorage } from '@angular/fire/storage';
import { provideMessaging, getMessaging } from '@angular/fire/messaging';
import { provideFirestore, getFirestore } from '@angular/fire/firestore';
import { FcmEffects } from './state/effects/fcm.effects';

swiperRegister();

function initializeAppFactory(authService: AuthService) {
  return () => authService.init();
}

@NgModule({
  declarations: [
    AppComponent,
    HomeComponent,
    DappComponent,
    ExploreComponent,
    VaultComponent,
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    SharedModule,
    AppRoutingModule,
    ApiModule,
    StoreModule.forRoot({}),
    EffectsModule.forRoot([]),
    provideFirebaseApp(() => {
      try {
        return getApp('[DEFAULT]');
      } catch {
        return initializeApp(environment.firebaseConfig);
      }
    }),
    provideFirebaseApp(() => {
      try {
        return getApp('master');
      } catch {
        return initializeApp(environment.firebaseMasterConfig, 'master');
      }
    }),
    provideAuth(() => {
      const app = getApp('master');
      return getAuth(app);
    }),
    provideStorage(() => {
      const app = getApp('master');
      return getStorage(app);
    }),
    provideFirestore(() => {
      const app = getApp('master');
      return getFirestore(app);
    }),
    provideMessaging(() => {
      const app = getApp('[DEFAULT]');
      return getMessaging(app);
    }),
  ],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: initializeAppFactory,
      deps: [AuthService],
      multi: true,
    },
    {
      provide: ReadyGGApiConfiguration,
      useValue: {
        rootUrl: environment.apiUrl,
      },
    },
    provideStore(reducers, { metaReducers: [storageSyncMetaReducer] }),
    provideEffects([FcmEffects, UserEffects, MarketPlaceEffects, PaymentEffects]),
    provideStoreDevtools({ maxAge: 25, logOnly: environment.production }),
  ],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppModule { }